import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { IconAlertCircle, IconCircleCheck, IconX } from '@tabler/icons-react'
import { useI18n } from '../lib/i18n'

export type ToastData = { id: number; message: string; variant?: 'success' | 'error' }

type ToastContextValue = { showToast: (message: string, variant?: ToastData['variant']) => void }

const ToastContext = createContext<ToastContextValue | null>(null)

/* Call from any component under <ToastProvider> to pop a bottom-right toast. */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within a ToastProvider')
  return ctx
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastData[]>([])

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id))
  }, [])

  const showToast = useCallback((message: string, variant: ToastData['variant'] = 'success') => {
    setToasts((list) => [...list, { id: Date.now() + Math.random(), message, variant }])
  }, [])

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="dash-toast-wrap">
        {toasts.map((t) => (
          <Toast key={t.id} toast={t} dismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

/* Bottom-right toast with a smooth slide+fade in/out and auto-dismiss. */
export function Toast({ toast, dismiss }: { toast: ToastData; dismiss: (id: number) => void }) {
  const { t } = useI18n()
  const [leaving, setLeaving] = useState(false)
  const [slotHeight, setSlotHeight] = useState<number | null>(null)
  const toastRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const element = toastRef.current
    if (!element) return
    const updateHeight = () => setSlotHeight(Math.ceil(element.getBoundingClientRect().height) + 10)
    updateHeight()
    const observer = new ResizeObserver(updateHeight)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  // Start the exit animation after a delay, then unmount once it finishes.
  useEffect(() => {
    const enter = setTimeout(() => setLeaving(true), 3400)
    return () => clearTimeout(enter)
  }, [])

  useEffect(() => {
    if (!leaving) return
    const exit = setTimeout(() => dismiss(toast.id), 420)
    return () => clearTimeout(exit)
  }, [dismiss, leaving, toast.id])

  const variant = toast.variant ?? 'success'

  return (
    <div
      className={`dash-toast-slot${leaving ? ' leaving' : ''}`}
      style={{ height: leaving ? 0 : slotHeight ?? 'auto' }}
    >
      <div ref={toastRef} className={`dash-toast ${variant}${leaving ? ' leaving' : ''}`} role="status" aria-live="polite">
        <span className="dash-toast-icon">
          {variant === 'error' ? <IconAlertCircle size={18} /> : <IconCircleCheck size={18} />}
        </span>
        <span className="dash-toast-msg">{toast.message}</span>
        <button className="dash-toast-close" type="button" aria-label={t('common.close')} onClick={() => setLeaving(true)}>
          <IconX size={14} />
        </button>
      </div>
    </div>
  )
}
